import React, { useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Btn } from '../components';
import { fieldProps } from '../lib/a11y';
import { palette as T, alpha, type } from '../design';

// The form checks the email format itself (noValidate), so the error is tied to the email field
// and read out with it (#345, DS-3.2). A browser's own bubble is tied to nothing a reader can find.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_MISSING = 'Enter your email address.';
const EMAIL_BAD_FORMAT = 'Enter your email address in the form name@example.com.';
const PASSWORD_MISSING = 'Enter your password.';
const CODE_MISSING = 'Enter the 6-digit code from your authenticator app.';

const emailProblem = (email) => {
  if (!email.trim()) return EMAIL_MISSING;
  return EMAIL_FORMAT.test(email.trim()) ? '' : EMAIL_BAD_FORMAT;
};

const labelStyle = { display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, marginBottom: 6 };
const fieldErrorStyle = { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '6px 0 0' };
const inputStyle = (invalid) => ({
  width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid ' + (invalid ? T.errorRed : T.border),
  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box',
});

// `onAuthenticated` hands the signed-in user back to the app, which decides where they land —
// their dashboard, the page an expired sign-in interrupted, or the booking they came to make.
// `notice` says why they were sent here in the first place.
export function LoginPage({ setPage, onAuthenticated, notice = '' }) {
  const { login, loginMfa, forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  // Password recovery is a third mode of this card rather than its own page (TTT-57) — it reuses
  // the same email field, so the address they just tried to sign in with carries over.
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState('');
  // `error` is form-level and tied to no field: the server's answer, which stays vague on purpose.
  // `fieldErrors` holds the problems the form finds itself, each read out with its own field.
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const codeRef = useRef(null);

  const emailField = fieldProps('login-email', fieldErrors.email);
  const passwordField = fieldProps('login-password', fieldErrors.password);
  const codeField = fieldProps('login-code', fieldErrors.code);

  // Shows the problems found, moves focus to the first field that has one, and says whether any did.
  const reportFieldErrors = (found, order) => {
    setFieldErrors(found);
    const first = order.find(([name]) => found[name]);
    if (first) first[1].current?.focus();
    return Boolean(first);
  };

  const clearFieldError = (name) => setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const found = { email: emailProblem(email), password: password ? '' : PASSWORD_MISSING };
    if (reportFieldErrors(found, [['email', emailRef], ['password', passwordRef]])) return;
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setShowMfa(true);
        setLoading(false);
        return;
      }
      onAuthenticated(result.user);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (reportFieldErrors({ code: mfaCode.trim() ? '' : CODE_MISSING }, [['code', codeRef]])) return;
    setLoading(true);
    try {
      const user = await loginMfa(mfaToken, mfaCode);
      onAuthenticated(user);
    } catch (err) {
      setError(err.message || 'That code is not correct. Enter the current code from your authenticator app.');
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (reportFieldErrors({ email: emailProblem(email) }, [['email', emailRef]])) return;
    setLoading(true);
    try {
      const data = await forgotPassword(email);
      setForgotSent(data.message);
    } catch (err) {
      setError(err.message || 'Could not send a reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgot(false);
    setForgotSent('');
    setError('');
    setFieldErrors({});
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 8px' }}>
            {showForgot ? 'Forgot your password?' : showMfa ? 'Two-Factor Authentication' : 'Welcome back'}
          </h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut }}>
            {showForgot
              ? "Enter your account email and we'll send you a link to choose a new password."
              : showMfa ? 'Enter the 6-digit code from your authenticator app.' : 'Sign in to your Ropida account'}
          </p>
        </div>

        <form noValidate onSubmit={showForgot ? handleForgotSubmit : showMfa ? handleMfaSubmit : handleLoginSubmit} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px' }}>
          {/* Why they were sent here — an expired sign-in (TTT-80) or a booking waiting on a
              login (TTT-82). Gold, not red: it is information, not something they got wrong.
              A form field, not a snackbar, because a 4-second toast can disappear before the
              page is even read. */}
          {notice && !showForgot && (
            <div style={{ background: alpha('caramelGold', 'selection'), border: '1px solid ' + alpha('caramelGold', 'scrim'), borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.5 }}>{notice}</p>
            </div>
          )}

          {/* Form-level and tied to no field: "Invalid email or password." must not say which one
              was wrong (account enumeration), so it belongs to neither field. */}
          {error && (
            <div role="alert" style={{ background: alpha('errorRed', 'tint'), border: '1px solid ' + alpha('errorRed', 'selection'), borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{error}</p>
            </div>
          )}

          {forgotSent && (
            <div style={{ background: alpha('sageGreen', 'tint'), border: '1px solid ' + alpha('sageGreen', 'selection'), borderRadius: 12, padding: '14px 16px' }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.5 }}>{forgotSent}</p>
            </div>
          )}

          {!showMfa && !forgotSent && (
            <div style={{ marginBottom: showForgot ? 24 : 16 }}>
              <label {...emailField.labelProps} style={labelStyle}>Email</label>
              <input
                {...emailField.inputProps}
                ref={emailRef}
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
                placeholder="you@example.com"
                required
                autoFocus={showForgot}
                style={inputStyle(fieldErrors.email)}
              />
              {fieldErrors.email && <p {...emailField.errorProps} style={fieldErrorStyle}>{fieldErrors.email}</p>}
            </div>
          )}

          {!showMfa && !showForgot && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                <label {...passwordField.labelProps} style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setError(''); setFieldErrors({}); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>
              <input
                {...passwordField.inputProps}
                ref={passwordRef}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => { setPassword(e.target.value); clearFieldError('password'); }}
                placeholder="••••••••"
                required
                style={inputStyle(fieldErrors.password)}
              />
              {fieldErrors.password && <p {...passwordField.errorProps} style={fieldErrorStyle}>{fieldErrors.password}</p>}
            </div>
          )}

          {showMfa && (
            <div style={{ marginBottom: 24 }}>
              <label {...codeField.labelProps} style={labelStyle}>Authentication code</label>
              <input
                {...codeField.inputProps}
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={e => { setMfaCode(e.target.value); clearFieldError('code'); }}
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
                style={{ ...inputStyle(fieldErrors.code), textAlign: 'center', letterSpacing: 4 }}
              />
              {fieldErrors.code && <p {...codeField.errorProps} style={fieldErrorStyle}>{fieldErrors.code}</p>}
            </div>
          )}

          {!forgotSent && (
            <Btn style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
              {showForgot
                ? (loading ? 'Sending…' : 'Send reset link →')
                : loading ? (showMfa ? 'Verifying…' : 'Signing in…') : (showMfa ? 'Verify →' : 'Sign in →')}
            </Btn>
          )}

          {showForgot && (
            <p style={{ textAlign: 'center', marginTop: forgotSent ? 16 : 20, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>
              <button
                type="button"
                onClick={backToLogin}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, padding: 0 }}
              >
                ← Back to login
              </button>
            </p>
          )}

          {!showMfa && !showForgot && (
            <p style={{ textAlign: 'center', marginTop: 20, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>
              Don&apos;t have an account?{' '}
              <button type="button" onClick={() => setPage('register')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, padding: 0 }}>
                Create one
              </button>
            </p>
          )}

          {showMfa && (
            <p style={{ textAlign: 'center', marginTop: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>
              <button
                type="button"
                onClick={() => { setShowMfa(false); setMfaCode(''); setMfaToken(''); setError(''); setFieldErrors({}); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, padding: 0 }}
              >
                ← Back to login
              </button>
            </p>
          )}
        </form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>← Back to home</button>
        </div>
      </div>
    </div>
  );
}
